import axios from 'axios';
import { getToken } from './auth';
import { markReported } from './reportedErrors';

export const BaseURL = process.env.REACT_APP_API_URL;

// SSRF prevention: the client has a fixed baseURL that request input cannot override.
const apiClient = axios.create({ baseURL: BaseURL });

const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') {
        throw new Error('Invalid URL parameter');
    }

    const sanitizedUrl = url.replace(/^\/+/, '');

    if (
        sanitizedUrl.startsWith('//') ||
        sanitizedUrl.includes('://') ||
        sanitizedUrl.includes('..') ||
        sanitizedUrl.includes('\\') ||
        /^[a-z]+:/i.test(sanitizedUrl)
    ) {
        throw new Error('Invalid URL: Only relative API paths are allowed');
    }

    if (!/^[a-zA-Z0-9\-_/?&=.%]+$/.test(sanitizedUrl)) {
        throw new Error('URL contains invalid characters');
    }

    return sanitizedUrl;
};

let errorHandlerRef = null;

export const registerErrorHandler = (fn) => {
    errorHandlerRef = fn;
};

// Marks the error so components don't show a second toast for it.
const reportError = (type, error) => {
    if (!errorHandlerRef) return;
    errorHandlerRef({ type });
    markReported(error);
};

let setLoadingRef = null;
let activeRequests = 0;

export const registerLoadingSetter = (fn) => {
    setLoadingRef = fn;
};

const showLoader = () => {
    activeRequests++;
    setLoadingRef?.(true);
};

const hideLoader = () => {
    activeRequests = Math.max(activeRequests - 1, 0);
    if (activeRequests === 0) {
        setLoadingRef?.(false);
    }
};

// Keeps the loader visible until `promise` settles, e.g. while a search result is polled.
export const trackLoading = async (promise) => {
    showLoader();
    try {
        return await promise;
    } finally {
        hideLoader();
    }
};

// options: { params, signal, skipLoader }. Resolves with the axios response.
const ApiCall = async (url, method, data, options = {}) => {
    if (!BaseURL) {
        throw new Error('REACT_APP_API_URL is not set. Check the .env files.');
    }

    const sanitizedUrl = isValidUrl(url);

    let token;
    try {
        token = await getToken();
    } catch (error) {
        reportError('auth', error);
        throw error;
    }

    const isGet = method.toString().toUpperCase() === 'GET';

    if (!options.skipLoader) showLoader();

    try {
        // SSRF-safe: apiClient has a fixed baseURL and sanitizedUrl is a validated relative path.
        // nosemgrep: javascript.lang.security.audit.ssrf.axios-ssrf.axios-ssrf
        return await apiClient.request({
            url: sanitizedUrl,
            method: method.toString().toLowerCase(),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            params: options.params,
            data: isGet ? undefined : data,
            signal: options.signal,
        });
    } catch (err) {
        if (err?.response?.status === 401) {
            // The old app cleared localStorage on 401 too (it also resets the PII warning).
            localStorage.clear();
            reportError('unauthorized', err);
        }
        throw err;
    } finally {
        if (!options.skipLoader) hideLoader();
    }
};

export default ApiCall;
